import { v1ApiClient } from './api.js';

// Hospital & Fleet Onboarding Service
// Manages local draft persistence, user-scoped storage, step validation, and submission simulation

export const initialOnboardingData = {
  // Step 1: Hospital Profile
  hospital_name: '',
  hospital_types: [], // Multi-speciality, Trauma Center, etc.
  city: '',
  state: '',
  pincode: '',
  number_of_beds: '',
  google_coordinates: '',
  google_maps_link: '',
  full_address: '',

  // Step 2: Facilities & Contacts
  emergency_facilities: [], // Emergency Department, Trauma Center, ICU, NICU / PICU, Cath Lab
  icu_beds: '',
  emergency_beds: '',
  total_beds: '',
  contacts: [
    {
      id: 'contact-1',
      role: '',
      name: '',
      mobile: '',
      email: '',
      is_primary: 'No'
    }
  ],

  // Step 3: Ambulance Requirements
  monthly_demands: [
    {
      id: 'month-1',
      month: '',
      ambulance_type: '',
      total: '',
      inbound: '',
      outbound: '',
      unserved: ''
    }
  ],
  peak_time_slots: [], // 12 AM–6 AM, 6 AM–12 PM, 12 PM–6 PM, 6 PM–12 AM
  custom_peak_time: '',
  peak_days: '',
  approx_peak_calls_per_hour: '',
  unserved_delay_reasons: [],
  unserved_delay_other_reason: '',
  ambulance_mix: {},

  // Step 4: Areas & Destinations
  area_demands: [
    {
      id: 'area-1',
      area: '',
      pincode: '',
      city: '',
      calls_per_month: '',
      inbound_percent: '',
      primary_type: '',
      current_supply: ''
    }
  ],
  destination_corridors: [
    {
      id: 'dest-1',
      destination: '',
      trips_per_month: '',
      distance_km: ''
    }
  ],
  service_gaps: [],
  service_gap_details: '',

  // Meta
  confirmed_accurate: false,
  status: 'NOT_STARTED', // NOT_STARTED | IN_PROGRESS | SUBMITTED | APPROVED
  current_step: 1,
  partnerType: 'hospital'
};

export const initialFleetOnboardingData = {
  // Step 1: Fleet Owner Profile
  operator_name: '',
  contact_info: '',
  business_license: '',
  emergency_contact: '',
  whatsapp_number: '',
  is_whatsapp_same: false,
  gstin: '',
  pan: '',
  years_in_business: '',
  registered_address: '',
  address: '',
  full_address: '',
  city: '',
  state: '',
  pincode: '',
  service_area: '',

  // Step 2: Capacity & Coverage
  fleet_capacities: [
    {
      id: 'fc-1',
      ambulance_type: '',
      owned: '',
      attached: '',
      available_daily: ''
    }
  ],
  primary_base_location: '',
  primary_pincode: '',
  service_radius_km: '',
  operating_hours: '',
  intercity_available: '',
  operating_areas: [
    {
      id: 'oa-1',
      area_pin: '',
      available_ambulances: '',
      peak_availability: [],
      type_mix: []
    }
  ],

  // Step 3: Rides & Clients
  monthly_rides: {
    completed_rides: '',
    enquiries: '',
    lost_rides: '',
    average_fare: '',
    average_trip_km: '',
    data_confidence: ''
  },
  lost_ride_reasons: [],
  top_clients: [
    {
      id: 'tc-1',
      client: '',
      type: '',
      area: '',
      rides_month: '',
      average_fare: '',
      payment: ''
    }
  ],
  top_pickup_areas: [
    {
      id: 'pa-1',
      area_pin: '',
      peak_time: '',
      type: ''
    }
  ],
  long_distance_corridors: [
    {
      id: 'ld-1',
      pickup: '',
      destination: '',
      trips_month: '',
      km: '',
      typical_rate: ''
    }
  ],

  // Step 4: Rates
  active_rate_card: {
    ambulance_type: '',
    base_rate: '',
    per_km: '',
    others: ''
  },
  rate_cards: [],
  city_rate_cards: [],

  // Step 5: Add Ambulances
  ambulances: [],

  // Step 6: Review & Submit
  confirmed_accurate: false,
  status: 'NOT_STARTED',
  current_step: 1,
  partnerType: 'fleet'
};

// Strips sensitive credentials and personal identifiable information (PII) before saving to local storage
export const sanitizeLocalDraft = (data) => {
  if (!data || typeof data !== 'object') return {};
  const clean = { ...data };

  // 1. Remove credentials & temporary secrets
  delete clean.admin_temporary_password;
  delete clean.confirmPassword;
  delete clean.password;

  // 2. Remove personal identifiable information (PII)
  delete clean.admin_name;
  delete clean.admin_phone;
  delete clean.admin_email;
  delete clean.mobile;
  delete clean.emergency_contact;
  delete clean.whatsapp_number;
  delete clean.is_whatsapp_same;
  delete clean.gstin;
  delete clean.pan;
  delete clean.registered_address;
  delete clean.address;
  delete clean.full_address;
  delete clean.business_license;

  // 3. Sanitize contacts list (remove personal phone, email, names from localStorage)
  if (Array.isArray(clean.contacts)) {
    clean.contacts = clean.contacts.map((c) => ({
      id: c.id,
      role: c.role,
      is_primary: c.is_primary
    }));
  }

  // 4. Sanitize ambulances (remove driver personal phone, licence, name, documents from localStorage)
  if (Array.isArray(clean.ambulances)) {
    clean.ambulances = clean.ambulances.map((a) => ({
      id: a.id,
      ambulance_type: a.ambulance_type,
      make_model: a.make_model,
      // Correct field name: the frontend state uses manufacture_year, not year
      manufacture_year: a.manufacture_year ?? a.year,
      ownership: a.ownership,
      operating_status: a.operating_status,
      availability_zones: a.availability_zones,
      // Correct field name: the frontend state uses medical_equipment, not equipment
      medical_equipment: a.medical_equipment ?? a.equipment,
      technology: a.technology,
      registration_number: a.registration_number
    }));
  }

  return clean;
};

/**
 * buildBackendDraftPayload — builds the `data` object for
 * PATCH /onboarding/applications/:id.
 *
 * The backend draft is the application of record (it is what the reviewer sees
 * and what approval provisions from), so it must receive the COMPLETE form
 * state: contact names / mobiles / emails, ambulance driver details, documents,
 * addresses, etc. PII is only stripped for the browser copy (sanitizeLocalDraft)
 * — never here. The backend replaces arrays wholesale on merge, so stripping
 * fields from `contacts` or `ambulances` would wipe them from the stored
 * application.
 *
 * Only two kinds of keys are removed:
 *  - credentials, which must never be persisted in the draft
 *  - local / server-owned meta that is not form data (the server row already
 *    owns status, version, application code and review notes)
 */
const BACKEND_DRAFT_OMITTED_KEYS = [
  // Credentials
  'admin_temporary_password',
  'confirmPassword',
  'password',
  // Server-owned / local-only meta
  'application_id',
  'applicationId',
  'application_code',
  'status',
  'version',
  'review_notes',
  'last_saved_at',
  'fromPasswordReset',
  'isDirty',
  '_localOnly',
  'submitted_at',
  'created_at',
  'updated_at'
];

export const buildBackendDraftPayload = (data) => {
  if (!data || typeof data !== 'object') return {};
  const payload = { ...data };
  for (const key of BACKEND_DRAFT_OMITTED_KEYS) {
    delete payload[key];
  }

  // Explicitly remove hidden fields so they are never sent to the backend
  delete payload.google_maps_link;
  delete payload.ambulance_requirements;
  delete payload.operating_areas;
  delete payload.top_pickup_areas;
  if (Array.isArray(payload.fleet_capacities)) {
    payload.fleet_capacities = payload.fleet_capacities.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const { attached, ...rest } = item;
      return rest;
    });
  }

  return payload;
};

// Resolves user and application scoped storage key
const getStorageKey = (userId, applicationId) => {
  const safeUserId = userId ? String(userId).replace(/[^a-zA-Z0-9_-]/g, '_') : 'guest';
  const storedApp = typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : null;
  const targetAppId = applicationId || storedApp;
  const safeAppId = targetAppId ? String(targetAppId).replace(/[^a-zA-Z0-9_-]/g, '_') : 'draft';
  return `risa_onboarding_draft_${safeUserId}_${safeAppId}`;
};

const getSubmittedKey = (userId, applicationId) => {
  const safeUserId = userId ? String(userId).replace(/[^a-zA-Z0-9_-]/g, '_') : 'guest';
  const storedApp = typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : null;
  const targetAppId = applicationId || storedApp;
  const safeAppId = targetAppId ? String(targetAppId).replace(/[^a-zA-Z0-9_-]/g, '_') : 'submitted';
  return `risa_onboarding_submitted_${safeUserId}_${safeAppId}`;
};

class HospitalOnboardingService {
  /**
   * Register Partner via Backend API
   */
  async registerPartner(payload) {
    try {
      const response = await v1ApiClient.post('/onboarding/register', payload);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Registration failed';
      throw new Error(message);
    }
  }

  /**
   * Fetch Draft from Backend
   */
  async fetchDraft(applicationId) {
    try {
      const response = await v1ApiClient.get(`/onboarding/applications/${applicationId}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.warn('Could not fetch draft from backend, falling back to local state:', error);
      return null;
    }
  }

  /**
   * Save Draft to Backend
   */
  async saveDraftToBackend(applicationId, data, currentStep = 1, version = 1) {
    const sanitized = buildBackendDraftPayload(data);
    const response = await v1ApiClient.patch(`/onboarding/applications/${applicationId}`, {
      current_step: currentStep,
      data: sanitized,
      version
    });
    return response.data?.data || response.data;
  }

  /**
   * Submit Application to Backend
   */
  async submitToBackend(applicationId, confirmedAccurate = true) {
    try {
      const response = await v1ApiClient.post(`/onboarding/applications/${applicationId}/submit`, {
        confirmed_accurate: confirmedAccurate
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Submission failed';
      throw new Error(message);
    }
  }

  /**
   * Initialize a new onboarding draft directly (Command Center / Authenticated Users)
   */
  async initDraft(partnerType = 'hospital') {
    try {
      const response = await v1ApiClient.post('/onboarding/init', { partnerType });
      return response.data?.data || response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Draft initialization failed';
      throw new Error(message);
    }
  }

  /**
   * Fetch all applications created by the logged-in user
   */
  async getUserApplications() {
    try {
      const response = await v1ApiClient.get('/onboarding/applications');
      return response.data?.data || response.data || [];
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch user applications';
      throw new Error(message);
    }
  }

  /**
   * Delete an onboarding application
   */
  async deleteApplication(applicationId) {
    try {
      const response = await v1ApiClient.delete(`/onboarding/applications/${applicationId}`);
      return response.data?.data || response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to delete application';
      throw new Error(message);
    }
  }

  /**
   * Fetch Active Fleet Operators for dropdown
   * Fetch active Fleet Operators directly from Backend Lookup
   */
  async getFleetOperatorsLookup() {
    try {
      const response = await v1ApiClient.get('/onboarding/lookups/fleet-operators');
      const list = response.data?.data || response.data;
      if (Array.isArray(list) && list.length > 0) {
        return list;
      }
    } catch (error) {
      console.warn('Failed to fetch from /onboarding/lookups/fleet-operators, trying fallback:', error);
    }

    try {
      const fallbackRes = await v1ApiClient.get('/fleet/operators');
      const fallbackList = fallbackRes.data?.data || fallbackRes.data;
      if (Array.isArray(fallbackList) && fallbackList.length > 0) {
        return fallbackList;
      }
    } catch (err) {
      console.warn('Failed to fetch fleet operators from fallback:', err);
    }

    return [];
  }

  /**
   * Fetch Supported Ambulance Types directly from Backend
   */
  async getAmbulanceTypesLookup() {
    try {
      const response = await v1ApiClient.get('/onboarding/lookups/ambulance-types');
      const list = response.data?.data || response.data;
      if (Array.isArray(list) && list.length > 0) {
        return list;
      }
    } catch (error) {
      console.warn('Failed to fetch from /onboarding/lookups/ambulance-types, trying /ambulances/types:', error);
    }

    try {
      const fallbackRes = await v1ApiClient.get('/ambulances/types');
      const fallbackList = fallbackRes.data?.data || fallbackRes.data;
      if (Array.isArray(fallbackList) && fallbackList.length > 0) {
        return fallbackList;
      }
    } catch (err) {
      console.warn('Failed to fetch ambulance types from fallback:', err);
    }

    return [];
  }

  /**
   * Fetch Active Patient Conditions directly from Backend (Command Center options)
   */
  async getPatientConditionsLookup() {
    try {
      const response = await v1ApiClient.get('/onboarding/lookups/patient-conditions');
      const list = response.data?.data || response.data;
      if (Array.isArray(list) && list.length > 0) {
        return list;
      }
    } catch (error) {
      console.warn('Failed to fetch from /onboarding/lookups/patient-conditions, trying dispatch options:', error);
    }

    try {
      const fallbackRes = await v1ApiClient.get('/command-center-dispatch/patient-condition-options');
      const fallbackList = fallbackRes.data?.data || fallbackRes.data;
      if (Array.isArray(fallbackList) && fallbackList.length > 0) {
        return fallbackList;
      }
    } catch (err) {
      console.warn('Failed to fetch patient conditions from dispatch fallback:', err);
    }

    return [];
  }

  /**
   * Load draft data from localStorage, scoped by user ID
   */
  getDraft(userId, applicationId) {
    try {
      const storageKey = getStorageKey(userId, applicationId);
      const stored = localStorage.getItem(storageKey);
      const partnerType = localStorage.getItem('risa_partner_type');
      const base = partnerType === 'fleet'
        ? JSON.parse(JSON.stringify(initialFleetOnboardingData))
        : JSON.parse(JSON.stringify(initialOnboardingData));

      if (stored) {
        const parsed = JSON.parse(stored);
        // If previous application was already submitted, do not restore it as an active working draft
        if (parsed.status === 'SUBMITTED' || parsed.status === 'APPROVED') {
          return base;
        }

        // Automatic migration & cleanup of legacy plain-text drafts containing PII
        if (parsed.admin_phone || parsed.admin_email || parsed.mobile) {
          const sanitized = sanitizeLocalDraft(parsed);
          localStorage.setItem(storageKey, JSON.stringify(sanitized));
          return { ...base, ...sanitized };
        }

        return { ...base, ...parsed };
      }
      return base;
    } catch (e) {
      console.error('Failed to load onboarding draft from localStorage', e);
    }
    return JSON.parse(JSON.stringify(initialOnboardingData));
  }

  loadDraft(partnerType, userId) {
    if (partnerType && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('risa_partner_type', partnerType);
      } catch (e) {}
    }
    return this.getDraft(userId);
  }

  /**
   * Save draft data to localStorage without sensitive credentials or personal identifiable information (PII)
   */
  saveDraft(data, userId, applicationId) {
    try {
      const appId = applicationId || data?.application_id;
      const storageKey = getStorageKey(userId, appId);
      const sanitized = sanitizeLocalDraft(data);
      // Do not re-save submitted application as a working draft
      if (sanitized.status === 'SUBMITTED' || sanitized.status === 'APPROVED') {
        return { success: true };
      }
      const updated = {
        ...sanitized,
        application_id: appId,
        status: sanitized.status === 'NOT_STARTED' ? 'IN_PROGRESS' : sanitized.status,
        last_saved_at: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return { success: true, data: updated };
    } catch (e) {
      console.error('Failed to save onboarding draft to localStorage', e);
      return { success: false, error: e.message };
    }
  }

  /**
   * Clear draft data (Start over or logout)
   */
  clearDraft(userId, applicationId) {
    try {
      const storageKey = getStorageKey(userId, applicationId);
      const submittedKey = getSubmittedKey(userId, applicationId);
      localStorage.removeItem(storageKey);
      localStorage.removeItem(submittedKey);
      localStorage.removeItem('risa_current_application_id');
      // Clean legacy unscoped keys if present
      localStorage.removeItem('risa_onboarding_draft_guest');
      return { success: true };
    } catch (e) {
      console.error('Failed to clear onboarding draft', e);
      return { success: false, error: e.message };
    }
  }

  /**
   * Check if onboarding has been submitted
   */
  getSubmittedApplication(userId, applicationId) {
    try {
      const submittedKey = getSubmittedKey(userId, applicationId);
      const saved = localStorage.getItem(submittedKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to read submitted application from localStorage', e);
    }
    return null;
  }

  /**
   * Submit complete onboarding application
   */
  submitApplication(data, userId) {
    try {
      const randomId = typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID().slice(0, 8).toUpperCase()
        : Math.floor(100000 + Math.random() * 900000);
      const applicationId = `RISA-APP-${randomId}`;

      const submissionRecord = {
        application_id: applicationId,
        partnerType: data?.partnerType || 'hospital',
        status: 'SUBMITTED',
        submitted_at: new Date().toISOString()
      };

      const storageKey = getStorageKey(userId, applicationId);
      const submittedKey = getSubmittedKey(userId, applicationId);
      localStorage.setItem(submittedKey, JSON.stringify(submissionRecord));
      localStorage.setItem('risa_last_submitted_application_code', applicationId);
      // Remove working draft from storageKey so next onboarding form starts clean and empty
      localStorage.removeItem(storageKey);
      localStorage.removeItem('risa_current_application_id');
      return { success: true, data: submissionRecord };
    } catch (e) {
      console.error('Failed to submit onboarding application', e);
      return { success: false, error: e.message };
    }
  }

  /**
   * Per-step validation helper
   * Returns { isValid: boolean, errors: Record<string, string> }
   */
  validateStep(stepNumber, formData, partnerType = 'hospital') {
    const errors = {};

    if (partnerType === 'hospital') {
      switch (stepNumber) {
        case 1: {
          if (!formData.hospital_name?.trim()) {
            errors.hospital_name = 'Hospital name is required';
          }
          if (!formData.city?.trim()) {
            errors.city = 'City is required';
          }
          if (!formData.state?.trim()) {
            errors.state = 'State is required';
          }
          if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
            errors.pincode = 'PIN code must be exactly 6 digits';
          }
          if (formData.mobile && !/^\d{10}$/.test(formData.mobile.trim())) {
            errors.mobile = 'Mobile number must be a valid 10-digit number';
          }
          break;
        }

        case 2: {
          const primaryContact = formData.contacts?.find((c) => c.is_primary === 'Yes') || formData.contacts?.[0];
          if (primaryContact) {
            if (primaryContact.mobile && !/^\d{10}$/.test(primaryContact.mobile.trim())) {
              errors.contact_mobile = 'Contact mobile number must be 10 digits';
            }
            if (primaryContact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryContact.email.trim())) {
              errors.contact_email = 'Invalid contact email format';
            }
          }
          break;
        }

        case 3: {
          let totalMix = 0;
          let hasAny = false;

          if (formData.ambulance_mix && typeof formData.ambulance_mix === 'object') {
            Object.values(formData.ambulance_mix).forEach((v) => {
              if (v !== '' && v !== null && v !== undefined) {
                hasAny = true;
                totalMix += Number(v) || 0;
              }
            });
          }

          if (!hasAny) {
            // Check individual dynamic mix fields
            const mixKeys = Object.keys(formData).filter((k) => k.startsWith('mix_') && k.endsWith('_percent'));
            mixKeys.forEach((k) => {
              if (formData[k] !== '' && formData[k] !== null && formData[k] !== undefined) {
                hasAny = true;
                totalMix += Number(formData[k]) || 0;
              }
            });
          }

          if (hasAny && totalMix !== 100) {
            errors.ambulance_mix = `Ambulance mix percentages must sum to exactly 100% (currently ${totalMix}%)`;
          }
          break;
        }

        default:
          break;
      }
    } else {
      // Fleet validation
      switch (stepNumber) {
        case 1: {
          if (!formData.operator_name?.trim()) {
            errors.operator_name = 'Operator name is required';
          }
          if (!formData.city?.trim()) {
            errors.city = 'City is required';
          }
          if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
            errors.pincode = 'PIN code must be exactly 6 digits';
          }
          break;
        }

        case 2: {
          if (formData.primary_pincode && !/^\d{6}$/.test(formData.primary_pincode.trim())) {
            errors.primary_pincode = 'Primary PIN code must be exactly 6 digits';
          }
          break;
        }

        default:
          break;
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export const hospitalOnboardingService = new HospitalOnboardingService();
export default hospitalOnboardingService;
